'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { signInWithCredentials, signInWithGoogle, signInWithFacebook, resendVerificationEmail } from '@/lib/auth-actions'
import { Loader2, CheckCircle, AlertCircle, Mail } from 'lucide-react'
import { Logo } from '@/components/ui/logo'
import { SystemMessageBanner } from '@/components/system-message'
import { useTranslations } from 'next-intl'

interface LoginFormProps {
  googleEnabled: boolean
  facebookEnabled: boolean
}

export default function LoginForm({ googleEnabled, facebookEnabled }: LoginFormProps) {
  const t = useTranslations('auth.login')
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null)
  const [resendSuccess, setResendSuccess] = useState(false)
  const searchParams = useSearchParams()
  const callbackUrl = searchParams.get('callbackUrl') || undefined

  const hasOAuthProviders = googleEnabled || facebookEnabled

  // Handle URL parameters for verification status
  useEffect(() => {
    const verified = searchParams.get('verified')
    const urlError = searchParams.get('error')

    if (verified === 'true') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Synchronizing state with URL params on mount
      setSuccessMessage(t('emailVerified'))
    } else if (urlError === 'invalid_token') {

      setError(t('invalidToken'))
    } else if (urlError === 'token_expired') {

      setError(t('tokenExpired'))
    } else if (urlError === 'user_not_found') {

      setError(t('userNotFound'))
    }
  }, [searchParams, t])

  async function handleSubmit(formData: FormData) {
    setIsLoading(true)
    setError(null)
    setSuccessMessage(null)
    setUnverifiedEmail(null)
    setResendSuccess(false)

    const result = await signInWithCredentials(formData, callbackUrl)

    if (result?.error) {
      if (result.error === 'email_not_verified') {
        setUnverifiedEmail(result.email || formData.get('email') as string)
        setError(t('verifyEmailFirst'))
      } else {
        setError(result.error)
      }
      setIsLoading(false)
    }
  }

  async function handleResendVerification() {
    if (!unverifiedEmail) return

    setIsResending(true)
    setError(null)

    const result = await resendVerificationEmail(unverifiedEmail, callbackUrl)

    if (result?.error) {
      setError(result.error)
    } else if (result?.success) {
      setResendSuccess(true)
    }
    setIsResending(false)
  }

  async function handleGoogleSignIn() {
    await signInWithGoogle(callbackUrl)
  }

  async function handleFacebookSignIn() {
    await signInWithFacebook(callbackUrl)
  }

  // Build signup URL with callback if present
  const signupUrl = callbackUrl
    ? `/signup?callbackUrl=${encodeURIComponent(callbackUrl)}`
    : '/signup'

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 p-4">
      <div className="w-full max-w-md space-y-4">
        <SystemMessageBanner targetLocation="auth_login" disablePolling />
        <Card className="border-zinc-700 bg-zinc-900/80 backdrop-blur">
          <CardHeader className="text-center space-y-2">
          <div className="flex justify-center mb-2">
            <Logo size="lg" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">{t('title')}</CardTitle>
          <CardDescription className="text-zinc-400">
            {t('description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {successMessage && (
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4 shrink-0" />
              {successMessage}
            </div>
          )}

          {resendSuccess && (
            <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm flex items-center gap-2">
              <Mail className="h-4 w-4 shrink-0" />
              {t('verificationSent')}
            </div>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {error}
              </div>
              {unverifiedEmail && !resendSuccess && (
                <Button
                  type="button"
                  variant="link"
                  className="text-violet-400 hover:text-violet-300 p-0 h-auto mt-2"
                  onClick={handleResendVerification}
                  disabled={isResending}
                >
                  {isResending ? (
                    <>
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      {t('sending')}
                    </>
                  ) : (
                    t('resendVerification')
                  )}
                </Button>
              )}
            </div>
          )}

          <form action={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">{t('emailLabel')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder={t('emailPlaceholder')}
                required
                autoFocus
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-400"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">{t('passwordLabel')}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder={t('passwordPlaceholder')}
                required
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-400"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('signingIn')}
                </>
              ) : (
                t('signInButton')
              )}
            </Button>
          </form>

          {hasOAuthProviders && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full bg-zinc-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-zinc-900 px-2 text-zinc-400">{t('orContinueWith')}</span>
                </div>
              </div>

              <div className={`grid gap-4 ${googleEnabled && facebookEnabled ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {googleEnabled && (
                  <form action={handleGoogleSignIn}>
                    <Button
                      type="submit"
                      variant="outline"
                      className="w-full border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white"
                    >
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                        <path
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                          fill="#4285F4"
                        />
                        <path
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                          fill="#34A853"
                        />
                        <path
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                          fill="#FBBC05"
                        />
                        <path
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                          fill="#EA4335"
                        />
                      </svg>
                      Google
                    </Button>
                  </form>
                )}
                {facebookEnabled && (
                  <form action={handleFacebookSignIn}>
                    <Button
                      type="submit"
                      variant="outline"
                      className="w-full border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-white"
                    >
                      <svg className="mr-2 h-4 w-4" fill="#1877F2" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      Facebook
                    </Button>
                  </form>
                )}
              </div>
            </>
          )}
        </CardContent>
        <CardFooter className="flex justify-center">
          <p className="text-sm text-zinc-400">
            {t('noAccount')}{' '}
            <Link href={signupUrl} className="text-violet-400 hover:text-violet-300 font-medium">
              {t('signUpLink')}
            </Link>
          </p>
        </CardFooter>
        </Card>
      </div>
    </div>
  )
}
